import React, { useState } from 'react';
import { Eye, KeyRound, PlugZap, RefreshCw } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';
type Tool = {
  name: string;
  slug: string;
  category: string;
  accounts: number;
  account?: string;
  group?: string;
  method: 'OAuth' | 'API key' | 'Database' | 'Browser';
};
const TOOLS: Tool[] = [{
  name: 'Slack',
  slug: 'slack',
  category: 'Communication',
  accounts: 1,
  account: 'brightwave.slack.com',
  group: 'Everyone',
  method: 'OAuth'
}, {
  name: 'Gmail',
  slug: 'gmail',
  category: 'Communication',
  accounts: 2,
  group: 'Everyone',
  method: 'OAuth'
}, {
  name: 'Notion',
  slug: 'notion',
  category: 'Productivity',
  accounts: 1,
  account: 'Brightwave HQ',
  group: 'Giga Master',
  method: 'OAuth'
}, {
  name: 'Salesforce',
  slug: 'salesforce',
  category: 'CRM & Sales',
  accounts: 1,
  account: 'brightwave.my.salesforce.com',
  group: 'Giga Master',
  method: 'OAuth'
}, {
  name: 'Linear',
  slug: 'linear',
  category: 'Engineering',
  accounts: 1,
  account: 'Brightwave',
  group: 'Giga Master',
  method: 'OAuth'
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  category: 'Marketing',
  accounts: 1,
  account: 'Brightwave Growth',
  group: 'Giga Marketing',
  method: 'API key'
}, {
  name: 'Snowflake',
  slug: 'snowflake',
  category: 'Data',
  accounts: 1,
  account: 'bw_analytics',
  group: 'Giga Master',
  method: 'Database'
}, {
  name: 'Stripe',
  slug: 'stripe',
  category: 'Finance',
  accounts: 1,
  account: 'Brightwave Inc',
  group: 'NoContact Ops',
  method: 'API key'
}, {
  name: 'QuickBooks',
  slug: 'quickbooks',
  category: 'Finance',
  accounts: 0,
  method: 'Browser'
}, {
  name: 'Figma',
  slug: 'figma',
  category: 'Design',
  accounts: 0,
  method: 'OAuth'
}, {
  name: 'Intercom',
  slug: 'intercom',
  category: 'Support',
  accounts: 1,
  account: 'Brightwave Support',
  group: 'NoContact Ops',
  method: 'OAuth'
}, {
  name: 'Ahrefs',
  slug: 'ahrefs',
  category: 'Marketing',
  accounts: 0,
  method: 'Browser'
}];
const METHOD_STYLE: Record<Tool['method'], string> = {
  OAuth: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  'API key': 'border-amber-200 bg-amber-50 text-amber-700',
  Database: 'border-sky-200 bg-sky-50 text-sky-700',
  Browser: 'border-violet-200 bg-violet-50 text-violet-700'
};
const POINTS = [{
  icon: PlugZap,
  title: 'Anything your team already uses',
  copy: '4,000 tools in the directory, plus databases, API keys, and email-and-password logins for the ones without an API.'
}, {
  icon: KeyRound,
  title: 'Connected once, not per product',
  copy: 'Credentials live in Giga, not inside ChatGPT or Claude, so no single AI provider controls how your team reaches its tools.'
}, {
  icon: Eye,
  title: 'Owned centrally',
  copy: 'Every connection has an owner, a group, and a history. Admins can see what is connected and where company data can flow.'
}, {
  icon: RefreshCw,
  title: 'Kept alive',
  copy: 'Token refreshes are handled for you, and a one-click reconnect link goes out the moment something drops.'
}];
export function ConnectedAppsPillar() {
  const [tab, setTab] = useState<'all' | 'connected'>('all');
  const shown = tab === 'all' ? TOOLS : TOOLS.filter(t => t.accounts > 0);
  const connected = TOOLS.filter(t => t.accounts > 0).length;
  return <div data-id="element-263" data-mp="3j4jl9jvgeojz">
      <div className="mx-auto max-w-3xl text-center" data-id="element-264" data-mp="3j51h4fe7f0rd">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-265" data-mp="kll41q57zhi9">
          The foundation
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-266" data-mp="fnt4qb28wt41">
          Connect your tools once.
          <br data-id="element-267" data-mp="5wyekvlvp9db" />
          <span className="text-[#EA580C]" data-id="element-268" data-mp="5x88uvdyi2ot">Use them everywhere.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-269" data-mp="13cc94fi6clqu">
          Giga is the connection layer between your company's software and its
          AI. Everything on this page runs on top of it.
        </p>
      </div>

      {/* The real Connections screen rather than a diagram — the whole claim of
       this pillar is that the setup is ordinary and visible. */}
      <div className="mx-auto mt-12 max-w-[1000px] md:mt-16" data-id="element-270" data-mp="o0conxvsowjc">
        <AppChrome active="Connections" data-id="element-271" data-mp="96zm6hc0n1ts">
          <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-272" data-mp="e4jdoixq5b3o">
            <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-273" data-mp="e51hc0mpurlm">
              <div data-id="element-274" data-mp="11v58h8ipwyvq">
                <h3 className="font-title text-[22px] font-medium tracking-tight text-[#2B211A]" data-id="element-275" data-mp="11um2mq6ce0y2">
                  Connections
                </h3>
                <p className="mt-1 text-[13px] text-neutral-500" data-id="element-276" data-mp="s4kc7rqio6fz">
                  {connected} connected · 4,000 available
                </p>
              </div>
              <span className="rounded-lg border border-[#E7E0D4] px-3 py-1.5 text-[12.5px] font-bold text-[#2B211A]" data-id="element-277" data-mp="x1klscmkrrbu">
                Add custom tool
              </span>
            </div>

            <div className="mb-4 mt-5 flex items-center gap-5 border-b border-[#EFEAE1]" data-id="element-278" data-mp="1pl9ajbg9cqkm">
              {([['all', 'All integrations', TOOLS.length], ['connected', 'Connected', connected]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[13px] font-semibold transition-colors ${tab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-400 hover:text-[#2B211A]'}`} data-id="element-279" data-mp="358wg408af6mi">
                    {label}
                    <span className="text-[12px] font-medium text-neutral-400" data-id="element-280" data-mp="2mc4qza62dxaj">
                      {count}
                    </span>
                  </button>)}
            </div>

            <div className="grid gap-2.5 sm:grid-cols-2" data-id="element-281" data-mp="309tbggw7z2f2">
              {shown.map(t => <div key={t.name} className="flex items-center gap-3 rounded-2xl border border-[#EFEAE1] bg-white px-3.5 py-2.5" data-id="element-282" data-mp="2w8r93r70aa8v">
                  <img src={toolLogo(t.slug)} alt="" aria-hidden="true" className="size-[18px] shrink-0 object-contain" data-id="element-283" data-mp="fnjdnc8r2zqf" />
                  <span className="min-w-0 flex-1" data-id="element-284" data-mp="34f3d02dage8m">
                    <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-285" data-mp="34ebu9s4siazr">
                      {t.name}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 truncate text-[11.5px] text-neutral-500" data-id="element-286" data-mp="3echtg1z7p30x">
                      {t.accounts > 0 ? <>
                          <span className="size-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-287" data-mp="3ic0ecxr19e3r" />
                          {t.accounts === 1 ? t.account : `${t.accounts} accounts`} ·{' '}
                          {t.group}
                        </> : t.category}
                    </span>
                  </span>
                  <span className={`hidden shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold sm:inline ${METHOD_STYLE[t.method]}`} data-id="element-288" data-mp="2umxpb4u2kiz8">
                    {t.method}
                  </span>
                </div>)}
            </div>
          </div>
        </AppChrome>
      </div>

      <div className="mx-auto mt-14 grid max-w-[1000px] gap-x-10 gap-y-8 md:mt-16 md:grid-cols-2" data-id="element-289" data-mp="26x38ydajkca8">
        {POINTS.map(({
        icon: Icon,
        title,
        copy
      }) => <div key={title} className="flex gap-4" data-id="element-290" data-mp="1uh3mm5lj1y3z">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-[#EA580C]/25 bg-white text-[#EA580C]" data-id="element-291" data-mp="1qeqitmqcca9u">
              <Icon size={17} data-id="element-292" data-mp="1lf475hq5v235" />
            </span>
            <div data-id="element-293" data-mp="1ggswx5vyepke">
              <h3 className="font-title text-xl font-bold leading-snug text-[#2B211A]" data-id="element-294" data-mp="1ghc9s86qynrk">
                {title}
              </h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-295" data-mp="1boj8xveefzkr">
                {copy}
              </p>
            </div>
          </div>)}
      </div>
    </div>;
}